import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiHome,
  FiInbox,
  FiAlertCircle,
  FiWifiOff,
  FiMapPin,
  FiChevronRight,
  FiSearch,
  FiX,
  FiEye,
} from 'react-icons/fi';
import { LuDoorOpen } from 'react-icons/lu';
import DashboardLayout from '../../components/layout/DashboardLayout';
import SectionCard from '../../components/ui/SectionCard';
import ListItem from '../../components/ui/ListItem';
import EmptyState from '../../components/ui/EmptyState';
import StatCard from '../../components/ui/StatCard';
import { getHouses } from '../../services/houseService';
import { formatDateTime } from '../../utils/format';

/** Normaliza texto removiendo diacríticos/acentos y convirtiendo a minúsculas. */
function normalizeText(text) {
  return (text ?? '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Lista de viviendas ya registradas (`GET /v1/houses`), punto de
 * entrada para "Registrar una visita": el operativo elige la vivienda
 * sobre la que quiere capturar los formularios de visita.
 */
export default function HousesListView() {
  const navigate = useNavigate();
  const [houses, setHouses] = useState(null);
  const [error, setError] = useState(null);
  const [offline, setOffline] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let active = true;
    setError(null);
    setOffline(!navigator.onLine);
    getHouses()
      .then((data) => {
        if (active) setHouses(data);
      })
      .catch((err) => {
        if (!active) return;
        setHouses([]);
        setError(err.message || 'No se pudieron cargar las viviendas.');
      });
    return () => {
      active = false;
    };
  }, []);

  const filteredHouses = useMemo(() => {
    if (!houses) return [];
    const trimmed = query.trim();
    if (!trimmed) return houses;

    const terms = normalizeText(trimmed).split(/\s+/).filter(Boolean);

    return houses.filter((house) => {
      const addr = house?.address || {};
      const street = addr.street || '';
      const num = addr.exteriorNumber || '';
      const neighborhood = addr.neighborhood || '';
      const section = addr.electoralSection || '';
      const block = addr.block || '';
      const ref = addr.reference || '';

      const searchableString = normalizeText([
        street,
        num,
        `${street} ${num}`,
        neighborhood,
        `colonia ${neighborhood}`,
        `col ${neighborhood}`,
        section,
        `seccion ${section}`,
        `secc ${section}`,
        block,
        `manzana ${block}`,
        `mza ${block}`,
        `mz ${block}`,
        ref,
      ].join(' '));

      return terms.every((term) => searchableString.includes(term));
    });
  }, [houses, query]);

  const totalVisits = useMemo(() => {
    if (!houses) return 0;
    return houses.reduce((acc, h) => acc + (Number(h.visitsCount) || 0), 0);
  }, [houses]);

  const sectionTitle = houses
    ? query.trim()
      ? `Viviendas (${filteredHouses.length} de ${houses.length})`
      : `Viviendas (${houses.length})`
    : 'Viviendas';

  return (
    <DashboardLayout title="Registrar una visita" subtitle="Elige una vivienda">
      {offline && !error && (
        <div className="form-error form-error--muted" role="status">
          <FiWifiOff /> Sin conexión: mostrando viviendas guardadas en este dispositivo.
        </div>
      )}

      {error && (
        <div className="form-error" role="alert">
          <FiAlertCircle /> {error}
        </div>
      )}

      {houses !== null && houses.length > 0 && (
        <section className="kpi-grid">
          <StatCard
            icon={<FiHome />}
            value={houses.length}
            label="Viviendas registradas"
          />
          <StatCard
            icon={<LuDoorOpen />}
            value={totalVisits}
            label="Visitas registradas"
            tone="success"
          />
        </section>
      )}

      <div className="search-box">
        <FiSearch aria-hidden="true" />
        <input
          type="text"
          placeholder="Buscar por calle, número, colonia o sección…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setQuery('');
          }}
          aria-label="Buscar vivienda"
        />
        {query && (
          <button
            type="button"
            className="search-box__clear"
            onClick={() => setQuery('')}
            aria-label="Limpiar búsqueda"
            title="Limpiar búsqueda"
          >
            <FiX />
          </button>
        )}
      </div>

      <SectionCard title={sectionTitle}>
        {houses === null && <EmptyState icon={<FiHome />} title="Cargando viviendas…" />}
        {houses !== null && houses.length === 0 && !error && (
          <EmptyState
            icon={<FiInbox />}
            title="Aún no has registrado viviendas"
            description={
              offline
                ? 'No hay viviendas guardadas en este dispositivo todavía.'
                : 'Registra una vivienda para poder capturar visitas sobre ella.'
            }
          />
        )}
        {houses !== null && houses.length > 0 && filteredHouses.length === 0 && (
          <EmptyState
            icon={<FiSearch />}
            title="Sin resultados"
            description={`No encontramos viviendas que coincidan con "${query.trim()}".`}
          />
        )}
        {filteredHouses.map((house) => {
          const percentage = house.progressPercentage ?? 0;
          const status =
            percentage >= 70 ? 'success' : percentage >= 50 ? 'warning' : 'danger';

          return (
            <ListItem
              key={house.id}
              onClick={() => navigate(`/data-entry/houses/${house.id}/visit`)}
              style={{ cursor: 'pointer' }}
              icon={<FiMapPin />}
              title={`${house.address.street} ${house.address.exteriorNumber}`.trim() || 'Vivienda sin dirección'}
              meta={
                <>
                  <span>
                    {`${house.address.neighborhood} · Sección ${house.address.electoralSection}`}
                    {` · ${house.visitsCount} ${house.visitsCount === 1 ? 'visita' : 'visitas'}`}
                    {house.lastVisit ? ` · Última: ${formatDateTime(house.lastVisit)}` : ''}
                  </span>
                  <div
                    className="house-progress"
                    role="progressbar"
                    aria-valuenow={percentage}
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-label={`Llenado: ${percentage}%`}
                  >
                    <div className="house-progress__track">
                      <div
                        className={`house-progress__fill house-progress__fill--${status}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className={`house-progress__label house-progress__label--${status}`}>
                      {percentage}%
                    </span>
                  </div>
                </>
              }
              right={
                <>
                  <button
                    type="button"
                    className="list-item__action"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/data-entry/houses/${house.id}/answers`);
                    }}
                    title="Ver respuestas guardadas"
                    aria-label="Ver respuestas guardadas"
                  >
                    <FiEye />
                  </button>
                  <FiChevronRight />
                </>
              }
            />
          );
        })}
      </SectionCard>
    </DashboardLayout>
  );
}
